'use client';

import React, { useEffect, useRef, useState } from 'react';
import type { CardTemplate } from '../templates/types';
import { differsFromThumbnailLocale, renderTemplatePreview } from './template-preview-render';

/**
 * A template's preview in the visitor's language.
 *
 * Draws the template live (template-preview-render.ts) once the tile scrolls
 * near the viewport, and shows the pre-rendered thumbnail only while that is
 * loading or if it fails. When the visitor's text is the same as the
 * thumbnail's (the Slovenian shop) the thumbnail is used as-is and nothing is
 * rendered.
 *
 * Fills its positioned parent (absolute, inset 0), like the <img> it replaced.
 */
export interface TemplateThumbProps {
  template: Pick<CardTemplate, 'id' | 'slots' | 'thumbnailUrl'>;
  locale: string;
  alt: string;
  /** Draw on this page size instead of the template's own (the product's printSpec). */
  canvas?: { widthMm: number; heightMm: number };
  /** object-fit for the image; previews are whole pages, so `cover` on a tile of the same ratio. */
  fit?: 'cover' | 'contain';
  /** Load eagerly (e.g. a single hero image) rather than when scrolled near. */
  eager?: boolean;
}

export default function TemplateThumb({ template, locale, alt, canvas, fit = 'cover', eager = false }: TemplateThumbProps) {
  const ref = useRef<HTMLDivElement>(null);
  const frontSlot = template.slots.find((s) => s.slotKey === 'front') ?? template.slots[0];
  const needsLive = !template.thumbnailUrl || differsFromThumbnailLocale(frontSlot, locale);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [visible, setVisible] = useState(eager);
  // A static thumbnail that failed to load (expired signature, missing file).
  const [staticBroken, setStaticBroken] = useState(false);
  const [forceLive, setForceLive] = useState(false);
  // The live render could not be drawn (or threw) — the stored thumbnail is
  // all there is.
  const [liveFailed, setLiveFailed] = useState(false);
  const wantLive = needsLive || forceLive;

  useEffect(() => {
    if (!wantLive || visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      // Start a little before the tile scrolls in so it is usually ready.
      { rootMargin: '200px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [wantLive, visible]);

  useEffect(() => {
    if (!wantLive || !visible || !frontSlot) return;
    let active = true;
    const cssWidth = ref.current?.clientWidth || 160;
    const dpr = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    renderTemplatePreview(
      frontSlot,
      locale,
      cssWidth * dpr,
      `${template.id}|${(template.thumbnailUrl ?? '').split('?')[0]}`,
      canvas,
    ).then(
      (url) => {
        if (!active) return;
        if (url) setLiveUrl(url);
        else setLiveFailed(true);
      },
      () => {
        if (active) setLiveFailed(true);
      },
    );
    return () => {
      active = false;
    };
    // frontSlot identity follows the template; canvas dims are primitives below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantLive, visible, locale, template.id, template.thumbnailUrl, canvas?.widthMm, canvas?.heightMm]);

  // While a translated preview is being drawn, show nothing rather than the
  // stored (Slovenian) thumbnail: on a phone the renders queue up behind a
  // fast scroll, and the Slovak shop flashed Slovenian invitations for seconds.
  // The stored image is used only when the live render fails.
  const waitingForLive = needsLive && !liveFailed;
  const src = liveUrl ?? (staticBroken || waitingForLive ? null : template.thumbnailUrl ?? null);

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0 }} aria-hidden={src ? undefined : true}>
      {src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => {
            if (src === liveUrl) return;
            // The static file is gone: draw the template instead.
            setStaticBroken(true);
            setForceLive(true);
          }}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: fit,
            display: 'block',
          }}
        />
      )}
    </div>
  );
}
